import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { publicUnsubscribe, unsubscribeFeedback } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

// Optional post-unsubscribe feedback reasons (captured AFTER opt-out is done).
const FEEDBACK_REASONS = [
  "Too many emails",
  "Not relevant to me",
  "I never signed up",
  "Content wasn't useful",
  "Other",
];

export default function Unsubscribe() {
  const { token } = useParams();
  const [state, setState] = useState("processing"); // processing | done | error
  const [company, setCompany] = useState("Rendeavored");
  const [feedback, setFeedback] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [sending, setSending] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard React StrictMode double-invoke
    ran.current = true;
    publicUnsubscribe(token)
      .then((d) => {
        if (d?.company) setCompany(d.company);
        setState("done");
      })
      .catch(() => setState("error"));
  }, [token]);

  const submitFeedback = async () => {
    if (!feedback) return;
    setSending(true);
    try {
      await unsubscribeFeedback(token, feedback);
      setFeedbackSent(true);
    } catch {
      setFeedbackSent(true); // best-effort; never block the recipient
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen rd-bg text-white flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-12 relative rd-noise">
        <div className="absolute inset-0 rd-glow pointer-events-none" />
        <div className="relative w-full max-w-md flex flex-col items-center">
          <Logo height={40} themeAware />

          <div
            className="mt-8 w-full rounded-2xl border border-white/10 p-8 text-center"
            style={{ backgroundColor: "#0F1424" }}
            data-testid="unsubscribe-card"
          >
            {state === "processing" && (
              <div className="flex flex-col items-center py-6" data-testid="unsubscribe-processing">
                <Loader2 className="animate-spin text-white/50" size={28} />
                <p className="mt-4 text-white/60 text-sm">Processing your request&hellip;</p>
              </div>
            )}

            {state !== "processing" && (
              <>
                <div className="flex justify-center">
                  <span className="h-14 w-14 rounded-full bg-[#2EE59D]/15 text-[#2EE59D] flex items-center justify-center">
                    <CheckCircle2 size={30} />
                  </span>
                </div>
                <h1 className="mt-5 text-2xl font-bold" data-testid="unsubscribe-title">
                  You&rsquo;ve been unsubscribed.
                </h1>
                <p className="mt-2 text-white/60 text-sm leading-relaxed" data-testid="unsubscribe-message">
                  You will no longer receive marketing emails from {company}.
                </p>

                {/* Optional feedback — shown only AFTER the opt-out is effective. */}
                <div className="mt-7 pt-6 border-t border-white/10 text-left">
                  {feedbackSent ? (
                    <p className="text-center text-[#2EE59D] text-sm" data-testid="unsubscribe-feedback-thanks">
                      Thanks for the feedback.
                    </p>
                  ) : (
                    <>
                      <p className="text-white/70 text-sm mb-3 text-center">
                        Mind sharing why you unsubscribed? <span className="text-white/40">(optional)</span>
                      </p>
                      <Select value={feedback} onValueChange={setFeedback}>
                        <SelectTrigger
                          className="bg-[#0A0F1E] border-white/10 text-white h-11"
                          data-testid="unsubscribe-feedback-select"
                        >
                          <SelectValue placeholder="Choose a reason" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                          {FEEDBACK_REASONS.map((r) => (
                            <SelectItem key={r} value={r} className="focus:bg-white/10 focus:text-white">
                              {r}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        onClick={submitFeedback}
                        disabled={!feedback || sending}
                        className="w-full mt-3 bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-11"
                        data-testid="unsubscribe-feedback-submit"
                      >
                        {sending ? <Loader2 className="animate-spin" size={16} /> : "Send feedback"}
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          <p className="mt-7 text-center text-xs text-white/35" data-testid="unsubscribe-attribution">
            TeamBuilder AI by Rendeavored
          </p>
        </div>
      </div>
    </div>
  );
}
